// ** React Imports
import { useState, createContext, useEffect } from 'react'
/* ************************************************ */
import { IntlProvider } from 'react-intl'
import flatten from 'flat'
import _ from "lodash"
/* ************************************************ */
import { _changeAppLang, _setAppLang, _setGlobal, env } from '../Utils'
import { genLang } from '@src/assets/data/locales'
import { userLang } from '@modules/user'
import { settingLang } from '@modules/setting'
import { logLang } from '@modules/log'
import { useRTL } from "../hooks/useRTL"

// ** Create Context
const Context = createContext()

const IntlProviderWrapper = ({ children, additionalLangs = {} }) => {

  // Safely extract common locales and keep the rest without mutating props
  const { ar = {}, cs = {}, de = {}, en = {}, es = {}, fr = {}, hi = {}, id = {}, it = {}, nl = {}, pl = {}, pt = {}, ru = {}, sk = {}, tr = {}, ur = {}, ...restAdditional } = additionalLangs || {}

  // ** Menu msg obj
  const menuMessages = {
    ar: { ...genLang.ar, ...userLang.ar, ...logLang.ar, ...settingLang.ar, ...ar },
    cs: { ...genLang.cs, ...userLang.cs, ...logLang.cs, ...settingLang.cs, ...cs },
    de: { ...genLang.de, ...userLang.de, ...logLang.de, ...settingLang.de, ...de },
    en: { ...genLang.en, ...userLang.en, ...logLang.en, ...settingLang.en, ...en },
    es: { ...genLang.es, ...userLang.es, ...logLang.es, ...settingLang.es, ...es },
    fr: { ...genLang.fr, ...userLang.fr, ...logLang.fr, ...settingLang.fr, ...fr },
    hi: { ...genLang.hi, ...userLang.hi, ...logLang.hi, ...settingLang.hi, ...hi },
    id: { ...genLang.id, ...userLang.id, ...logLang.id, ...settingLang.id, ...id },
    it: { ...genLang.it, ...userLang.it, ...logLang.it, ...settingLang.it, ...it },
    nl: { ...genLang.nl, ...userLang.nl, ...logLang.nl, ...settingLang.nl, ...nl },
    pl: { ...genLang.pl, ...userLang.pl, ...logLang.pl, ...settingLang.pl, ...pl },
    pt: { ...genLang.pt, ...userLang.pt, ...logLang.pt, ...settingLang.pt, ...pt },
    ru: { ...genLang.ru, ...userLang.ru, ...logLang.ru, ...settingLang.ru, ...ru },
    sk: { ...genLang.sk, ...userLang.sk, ...logLang.sk, ...settingLang.sk, ...sk },
    tr: { ...genLang.tr, ...userLang.tr, ...logLang.tr, ...settingLang.tr, ...tr },
    ur: { ...genLang.ur, ...userLang.ur, ...logLang.ur, ...settingLang.ur, ...ur },
    ...restAdditional
  }
  // ** States
  const defaultLang = env('REACT_APP_DEFAULT_LANG', 'en')
  const localStorageLang = localStorage.getItem('APP_LANG')
  const lang = localStorageLang ? localStorageLang : defaultLang
  const [locale, setLocale] = useState(lang)

  // Choose current messages with proper fallback
  const currentMenuMessages = menuMessages[locale] || menuMessages[defaultLang] || {}
  const [messages, setMessages] = useState(flatten(currentMenuMessages))
  _setGlobal('messages', messages)
  const [isRtl, setIsRtl] = useRTL()
  _setAppLang(lang)

  useEffect(() => {
    if (locale === 'ar' || locale === 'ur') {
      setIsRtl(true)
    } else {
      setIsRtl(false)
    }
    return () => {
    }
  }, [locale, setIsRtl])

  // Update flattened messages when locale or menuMessages change
  useEffect(() => {
    const next = menuMessages[locale] || menuMessages[defaultLang] || {}
    setMessages(flatten(next))
  }, [locale, defaultLang, additionalLangs])

  // ** Switches Language
  const switchLanguage = lang => {
    _changeAppLang(lang)
  }

  // ** Plain text getter (non-intl)
  const plainText = key => {
    return _.get(menuMessages, `${locale}.${key}`) ?? key
  }

  return (
    <Context.Provider value={{ locale, switchLanguage, plainText }}>
      <IntlProvider key={locale} locale={locale} messages={messages} defaultLocale={defaultLang}>
        {children}
      </IntlProvider>
    </Context.Provider>
  )
}

export { IntlProviderWrapper, Context as IntlContext }
